import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { createMenu } from '../../../shared/api/menus.js'
import type { Menu } from '../../../shared/types/models.js'
import { validateNameField } from '../../../shared/utils/catalog.js'
import { toFieldErrors, type FieldErrors } from '../../../shared/utils/formErrors.js'
import { ROUTES } from '../../../app/routes.js'

interface Props {
  organizationId: string
  onCancel: () => void
}

export default function CreateMenuForm({ organizationId, onCancel }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [saving, setSaving] = useState(false)

  function handleChange() {
    if (errors.name || errors.general) setErrors({})
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()

    const fieldErrors: FieldErrors = {}
    const nameError = validateNameField(name)
    if (nameError === 'required') fieldErrors.name = t('error.fieldRequired')
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    setSaving(true)
    setErrors({})
    try {
      const menu: Menu = await createMenu(organizationId, {
        name: name.trim(),
        slug: slug.trim() || undefined,
        description: description.trim() || undefined,
      })
      navigate(`${ROUTES.MENU}/${menu.id}`)
    } catch (err: unknown) {
      setErrors(toFieldErrors(err, ['name', 'slug'], t('error.serverError')))
      setSaving(false)
    }
  }

  return (
    <div className="settings-card create-card">
      <h3>{t('menu.createTitle')}</h3>
      <p className="page-section-description">{t('menu.createSubtitle')}</p>

      {errors.general && <div className="form-error-banner">{errors.general}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-field">
          <label htmlFor="menuName">{t('menu.name')} *</label>
          <input
            id="menuName"
            type="text"
            value={name}
            onChange={e => {
              setName(e.target.value)
              handleChange()
            }}
            maxLength={200}
            required
            disabled={saving}
            autoComplete="off"
          />
          {errors.name && <span className="field-error">{errors.name}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="menuSlug">{t('menu.slug')}</label>
          <input
            id="menuSlug"
            type="text"
            value={slug}
            onChange={e => {
              setSlug(e.target.value)
              handleChange()
            }}
            maxLength={100}
            disabled={saving}
            autoComplete="off"
          />
          <span className="field-hint">{t('menu.slugHint')}</span>
          {errors.slug && <span className="field-error">{errors.slug}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="menuDescription">{t('menu.description')}</label>
          <textarea
            id="menuDescription"
            value={description}
            onChange={e => {
              setDescription(e.target.value)
              handleChange()
            }}
            rows={3}
            maxLength={2000}
            disabled={saving}
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t('menu.creating') : t('menu.create')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
            {t('menu.cancel')}
          </button>
        </div>
      </form>
    </div>
  )
}